import { useController, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import type { IntlTelInputRef } from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";
import "./PhoneInput.css";

interface PhoneInputProps<TFieldValues extends FieldValues = FieldValues> {
  /** The field name in the form */
  name: FieldPath<TFieldValues>;
  /** React Hook Form control object */
  control: Control<TFieldValues>;
  /** Optional label for the phone input */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Validation rules for the field */
  rules?: Parameters<typeof useController<TFieldValues>>[0]["rules"];
  /** Whether the phone input is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Initial country code (e.g., "us", "es") */
  initialCountry?: string;
}

export default function PhoneInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  placeholder,
  rules,
  disabled,
  className = "",
  initialCountry = "pa",
}: PhoneInputProps<TFieldValues>) {
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules,
  });

  const intlTelInputRef = useRef<IntlTelInputRef>(null);

  // Sync external value changes to IntlTelInput
  useEffect(() => {
    const instance = intlTelInputRef.current?.getInstance();
    if (instance && field.value !== undefined) {
      const currentNumber = instance.getNumber();
      // Only update if the value is different to avoid unnecessary updates
      if (currentNumber !== field.value) {
        instance.setNumber(field.value || "");
      }
    }
  }, [field.value]);

  return (
    <div className={className}>
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
        </label>
      )}
      <div className={error ? "is-invalid" : ""}>
        <IntlTelInput
          ref={intlTelInputRef}
          initialValue={field.value || ""}
          onChangeNumber={(number) => {
            field.onChange(number || "");
          }}
          initOptions={{
            // @ts-expect-error - initialCountry type is strict but we accept string
            initialCountry: initialCountry,
            separateDialCode: true,
            containerClass: "intl-tel-input",
          }}
          inputProps={{
            id: name,
            className: "form-control less-rounded",
            placeholder: placeholder,
            disabled: disabled,
            onBlur: field.onBlur,
          }}
        />
      </div>
      {error && (
        <div className="invalid-feedback d-block">
          {error.message ? t(error.message) : ""}
        </div>
      )}
    </div>
  );
}
